import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ConcertRow, type PrefixedShared } from '../components/ConcertRow.tsx'
import { ConcertSearch } from '../components/ConcertSearch.tsx'
import { DayTabs } from '../components/DayTabs.tsx'
import { NowMarker } from '../components/NowMarker.tsx'
import { StageFilter } from '../components/StageFilter.tsx'
import { friendPrefix } from '../domain/friendName.ts'
import { concertsForDay, nextDay, previousDay } from '../domain/program.ts'
import { concertStatus, currentFestivalDay, formatDayLabel, nowMarkerIndex } from '../domain/time.ts'
import { useHorizontalSwipe } from '../hooks/useHorizontalSwipe.ts'
import { useNow } from '../hooks/useNow.ts'
import { t } from '../i18n.ts'
import { CONCERTS, DAYS, STAGES } from '../program.ts'
import { useAllAnnotations, useFriends, useStore } from '../store/StoreProvider.tsx'
import { AuthButton } from '../components/AuthButton.tsx'
import { landingDay } from '../App.tsx'
import './DayView.css'

/** Stable identity, so an unannotated row is not handed a fresh array every render. */
const NO_SHARED: readonly PrefixedShared[] = []

/**
 * Where a terningkast turns positive.
 *
 * Four, because that is what the idiom means: a Norwegian review reads 1–3 as a
 * panning and 4–6 as a recommendation. Nothing about the number is ours to tune.
 */
const POSITIVE_DICE = 4

/**
 * The shape `?tid=` has to have: a wall-clock `HH:MM`, exactly as the programme
 * stores a start time.
 *
 * Matching against it is then a plain string comparison and needs no parsing —
 * no concert starts after midnight, so lexical order over `HH:MM` *is*
 * chronological order, which is also what the list is sorted by. Anything that
 * does not fit is a hand-edited URL, and the day view ignores it rather than
 * scrolling somewhere it cannot justify.
 */
const TIME_PARAM = /^([01]\d|2[0-3]):[0-5]\d$/

export function DayView() {
  const { day = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const now = useNow()
  const store = useStore()
  const annotations = useAllAnnotations()
  const friends = useFriends()
  const [onlyLiked, setOnlyLiked] = useState(false)
  // The stage selection survives a day change on purpose: "what is on at Sirkus all
  // weekend" is a question people answer by swiping, and having to re-pick the stage
  // on every day would make the filter useless for exactly that.
  const [selectedStages, setSelectedStages] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  )
  const markerRef = useRef<HTMLLIElement | null>(null)
  const targetRef = useRef<HTMLLIElement | null>(null)

  const tid = searchParams.get('tid')
  const scrollToTime = tid !== null && TIME_PARAM.test(tid) ? tid : null

  const today = currentFestivalDay(DAYS, now)
  const isValidDay = DAYS.includes(day)

  const votes = useMemo(() => {
    const map = new Map<string, 'up' | 'down'>()
    for (const a of annotations) {
      if (a.vote) map.set(a.concertId, a.vote)
    }
    return map
  }, [annotations])

  const notes = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of annotations) {
      const note = a.note.trim()
      if (note) map.set(a.concertId, note)
    }
    return map
  }, [annotations])

  const dice = useMemo(() => {
    const map = new Map<string, number>()
    for (const a of annotations) {
      if (a.dice != null) map.set(a.concertId, a.dice)
    }
    return map
  }, [annotations])

  /**
   * What the filter counts as a liking: a thumbs-up, or a terningkast of 4+.
   *
   * One predicate rather than three copies of the condition, because three places
   * read it — the list, the switch's count, and every stage chip's count — and they
   * have to agree or a badge promises a number that tapping it does not deliver.
   * Both signals count: adopting the dice must not quietly cost someone the filter.
   */
  const isPositive = useCallback(
    (concertId: string) =>
      votes.get(concertId) === 'up' || (dice.get(concertId) ?? 0) >= POSITIVE_DICE,
    [votes, dice],
  )

  const allForDay = useMemo(() => concertsForDay(CONCERTS, day), [day])

  /*
   * The two filters compose, and each one's badge is counted against the *other* one
   * already applied. That is what makes the numbers trustworthy: a chip reading
   * "Sirkus 3" while the liked filter is on promises three concerts, and tapping it
   * delivers three — not four with one of them silently dropped.
   */
  const liked = useMemo(
    () => (onlyLiked ? allForDay.filter((c) => isPositive(c.id)) : allForDay),
    [allForDay, onlyLiked, isPositive],
  )
  const concerts = useMemo(
    () =>
      selectedStages.size === 0 ? liked : liked.filter((c) => selectedStages.has(c.stage)),
    [liked, selectedStages],
  )

  /**
   * The row a `?tid=` link is aiming at: the first one that has not started yet at
   * that time, which is the same slot the now-marker would take. One row, not a map
   * of all twenty-two — the ref only has to reach the one that gets scrolled to.
   *
   * Resolved against the *filtered* list, so a row the filters have removed is
   * simply not a target. Scrolling to a row that is not on screen would be worse
   * than not scrolling at all.
   */
  const targetId = useMemo(
    () => (scrollToTime ? (concerts.find((c) => c.start >= scrollToTime)?.id ?? null) : null),
    [concerts, scrollToTime],
  )

  const stagesForDay = useMemo(() => {
    const present = new Set(allForDay.map((c) => c.stage))
    return STAGES.filter((stage) => present.has(stage))
  }, [allForDay])

  const stageCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const concert of liked) map.set(concert.stage, (map.get(concert.stage) ?? 0) + 1)
    return map
  }, [liked])

  const likedCount = useMemo(() => {
    const scoped =
      selectedStages.size === 0
        ? allForDay
        : allForDay.filter((c) => selectedStages.has(c.stage))
    return scoped.filter((c) => isPositive(c.id)).length
  }, [allForDay, selectedStages, isPositive])

  /**
   * Who each uid belongs to, for the whole day at once.
   *
   * A map rather than a `find` per annotation: the same friend routinely annotates
   * half the day, and building it once per friend-list change is the difference
   * between one pass over the list and one per line rendered.
   */
  const friendsByUid = useMemo(
    () => new Map(friends.map((friend) => [friend.uid, friend])),
    [friends],
  )

  // Read through `annotations` rather than taking a second subscription:
  // `useAllAnnotations` above already re-renders on every store write, and
  // `replaceShared` is one of them.
  const sharedByConcert = useMemo(() => {
    const map = new Map<string, PrefixedShared[]>()
    for (const concert of concerts) {
      const forConcert = store.sharedAnnotations(concert.id)
      if (forConcert.length === 0) continue
      /*
       * The name is resolved here, not in the row, because it is decided by the
       * friend list and the row has no business subscribing to it twenty-two times
       * over. The address comes first (R14): it is what this user typed when they
       * added the person, while `ownerName` on the annotation is whatever Google
       * handed over — which is why the friend list carries addresses at all (KTD6).
       * The friend's own name outranks the annotation's for the same reason, and a
       * note from someone who is not a friend, arriving through an explicit share,
       * has only `ownerName` to go on.
       */
      map.set(
        concert.id,
        forConcert.map((annotation) => {
          const friend = friendsByUid.get(annotation.uid ?? '')
          return {
            ...annotation,
            prefix: friendPrefix({
              email: friend?.email,
              name: friend?.name ?? annotation.ownerName,
            }),
          }
        }),
      )
    }
    return map
  }, [concerts, store, annotations, friendsByUid])

  const toggleStage = (stage: string) => {
    setSelectedStages((current) => {
      const next = new Set(current)
      if (!next.delete(stage)) next.add(stage)
      return next
    })
  }

  const clearStages = () => setSelectedStages(new Set<string>())

  const clearFilters = () => {
    setOnlyLiked(false)
    clearStages()
  }

  const showMarker = day === today
  const markerIndex = showMarker ? nowMarkerIndex(concerts, now) : -1
  const clock = now.toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Oslo',
  })

  const goPrevious = () => {
    const target = previousDay(DAYS, day)
    if (target) navigate(`/dag/${target}`)
  }
  const goNext = () => {
    const target = nextDay(DAYS, day)
    if (target) navigate(`/dag/${target}`)
  }

  const swipe = useHorizontalSwipe({ onSwipeLeft: goNext, onSwipeRight: goPrevious })

  /*
   * Arriving from a concert's time: open the schedule at that moment.
   *
   * Keyed on the link rather than on the list, for the same reason the marker
   * effect below is keyed on the day — `useNow` re-renders this component every
   * minute, and re-scrolling on every tick would drag a reader who has since
   * scrolled elsewhere back to where they came in.
   */
  useEffect(() => {
    if (!scrollToTime) return
    targetRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [day, scrollToTime])

  // Scroll to the current time once per day view, not on every tick — re-scrolling
  // every minute would fight a user who has scrolled somewhere else on purpose.
  // A `?tid=` link outranks it: two effects scrolling the same list on the same
  // render is a race, and the one the reader just tapped has to win it.
  useEffect(() => {
    if (scrollToTime) return
    if (!showMarker) return
    const node = markerRef.current
    if (!node) return
    node.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [day, showMarker, scrollToTime])

  if (!isValidDay) return <Navigate to={`/dag/${landingDay()}`} replace />

  const filtering = onlyLiked || selectedStages.size > 0

  return (
    <div className="day-view">
      <DayTabs days={DAYS} activeDay={day} today={today} />

      <div className="page">
        {/* Above the day heading, because it answers a question about the whole
            programme rather than about the day showing. */}
        <ConcertSearch />

        <div className="day-view__head">
          <div>
            <h1 className="day-view__title">{formatDayLabel(day)}</h1>
            <p className="day-view__count">
              {filtering
                ? t.day.concertCountFiltered(concerts.length, allForDay.length)
                : t.day.concertCount(allForDay.length)}
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={onlyLiked}
            className="day-view__filter"
            data-on={onlyLiked || undefined}
            onClick={() => setOnlyLiked((v) => !v)}
          >
            {/* Still a thumb, though the filter now takes a good terningkast too: it
                is decorative and hidden from assistive tech, and one glyph reads as a
                verdict where two would read as a legend. */}
            <span className="day-view__filter-icon" aria-hidden="true">
              👍
            </span>
            <span>{t.day.onlyLikedShort}</span>
            {likedCount > 0 && <span className="day-view__filter-count">{likedCount}</span>}
          </button>
        </div>

        <StageFilter
          stages={stagesForDay}
          selected={selectedStages}
          counts={stageCounts}
          onToggle={toggleStage}
          onClear={clearStages}
        />

        <div
          className="day-view__swipe"
          {...swipe}
          role="group"
          aria-label={formatDayLabel(day)}
        >
          {concerts.length === 0 ? (
            <EmptyState
              onlyLiked={onlyLiked}
              stageFiltered={selectedStages.size > 0}
              onClearFilters={clearFilters}
            />
          ) : (
            <ul className="day-view__list">
              {concerts.map((concert, index) => (
                <Fragment key={concert.id}>
                  {index === markerIndex && <NowMarker ref={markerRef} time={clock} />}
                  <ConcertRow
                    ref={concert.id === targetId ? targetRef : undefined}
                    concert={concert}
                    status={concertStatus(concert, now)}
                    vote={votes.get(concert.id) ?? null}
                    dice={dice.get(concert.id) ?? null}
                    note={notes.get(concert.id) ?? ''}
                    shared={sharedByConcert.get(concert.id) ?? NO_SHARED}
                  />
                </Fragment>
              ))}
              {/* Everything for today has finished. */}
              {markerIndex === concerts.length && <NowMarker ref={markerRef} time={clock} />}
            </ul>
          )}
        </div>

        <AuthButton />
      </div>
    </div>
  )
}

/**
 * Why the list is empty, and the way out.
 *
 * The message names the filter that actually emptied it. "Ingen likte konserter" in
 * front of someone who has simply picked a quiet stage is worse than no message at
 * all — they would go looking for the wrong control.
 */
function EmptyState({
  onlyLiked,
  stageFiltered,
  onClearFilters,
}: {
  onlyLiked: boolean
  stageFiltered: boolean
  onClearFilters: () => void
}) {
  if (!onlyLiked && !stageFiltered) {
    return <p className="day-view__empty">{t.day.empty}</p>
  }

  const message = onlyLiked
    ? stageFiltered
      ? t.day.emptyLikedStages
      : t.day.emptyFiltered
    : t.day.emptyStages

  return (
    <div className="day-view__empty">
      <p>{message}</p>
      <button type="button" className="day-view__empty-action" onClick={onClearFilters}>
        {t.day.emptyFilteredAction}
      </button>
    </div>
  )
}
